import { gray } from '@veupathdb/coreui/lib/definitions/colors';
import { AiProvenance } from '../../../types/userCommentTypes';
import { LazyPubmedPreview } from './LazyPubmedPreview';
import { Row } from './CommentSectionRow';
import { TextDiff } from './TextDiff';

const GREY = '#888';
const LINK_BLUE = '#336f99';

interface Props {
  aiProvenance: AiProvenance;
  // The published (possibly author-edited) headline/content, diffed against
  // aiProvenance.originalHeadline/originalContent.
  headline: string;
  content: string;
}

// AI provenance shown below the comment content (the headline + content stay the
// visual priority; the "AI-assisted" pill in the header does the highlighting).
// Rendered as labelled rows in the same style as the other references, with the
// source under the standard "PubMed Article(s)" heading so AI and human comments
// match.
export function AiProvenanceSection({
  aiProvenance,
  headline,
  content,
}: Props): JSX.Element {
  const { isEdited, source, originalHeadline, originalContent } = aiProvenance;

  const howSentence = isEdited
    ? 'This summary was generated by AI from the source shown below, then edited by the author before publishing.'
    : 'This summary was generated by AI from the source shown below and published as generated (unedited).';

  return (
    <>
      <Row label="How this comment was generated">
        <div>{howSentence}</div>
        {isEdited && (
          <details style={{ marginTop: '6px' }}>
            <summary style={{ cursor: 'pointer', color: LINK_BLUE }}>
              Show edits to AI-generated text
            </summary>
            <div
              style={{
                marginTop: '6px',
                border: `1px solid ${gray[400]}`,
                borderRadius: 7,
                padding: '10px 14px',
              }}
            >
              <div style={{ fontWeight: 600, fontSize: '14px' }}>Headline</div>
              <div style={{ marginBottom: '8px' }}>
                <TextDiff before={originalHeadline} after={headline} />
              </div>
              <div style={{ fontWeight: 600, fontSize: '14px' }}>Content</div>
              <div>
                <TextDiff before={originalContent} after={content} />
              </div>
            </div>
          </details>
        )}
      </Row>

      {source.kind === 'pubmed' ? (
        <Row label="PubMed Article(s)">
          <LazyPubmedPreview pubmedId={source.pubmedId} />
        </Row>
      ) : (
        <>
          <Row label="Source publication">
            {source.externalUrl ? (
              <div>
                <a
                  href={source.externalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {source.externalTitle || source.externalUrl}
                </a>
              </div>
            ) : (
              <div>User-uploaded PDF — not publicly available</div>
            )}
            <div style={{ color: GREY, fontStyle: 'italic', fontSize: '13px' }}>
              (uploaded PDF, processed in the author's browser — file not
              stored)
            </div>
          </Row>
          {source.externalRef &&
            (source.externalRefKind === 'pubmed' ? (
              <Row label="PubMed Article(s)">
                <LazyPubmedPreview pubmedId={source.externalRef} />
              </Row>
            ) : (
              <Row label="DOI">
                <a
                  href={`https://doi.org/${source.externalRef}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  https://doi.org/{source.externalRef}
                </a>
              </Row>
            ))}
        </>
      )}
    </>
  );
}
